import React, { useEffect, useState } from 'react';
import { AgFinancingPhase4Status, AgGroup } from '@indigo-ag/schema';
import { ReadOnlyField } from '@indigo-ag/component-module';

import i18next from 'core/i18n';
import {
  NotificationSection,
  NotificationSectionProps
} from '../../BrazilGrowerFinance/shared/NotificationSection/NotificationSection';
import { text, UserUtil } from 'shared';

interface CessionGroupStateProps {
  cessionGroup: AgGroup;
}

const defaultNotificationProp: NotificationSectionProps = {
  title: '',
  status: 'warning'
};

export const CessionGroupState: React.FunctionComponent<CessionGroupStateProps> = ({ cessionGroup }) => {
  /**
   * State
   */
  const [notes, setNotes] = useState<string>();
  const [notificationProps, setNotificationProps] = useState<NotificationSectionProps>(defaultNotificationProp);

  useEffect(() => {
    setNotificationPropsAndNotes();
  }, [cessionGroup.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setNotificationPropsAndNotes = () => {
    const phase4Info = cessionGroup.agFinancingProcesses[0]?.phase4;

    switch (cessionGroup.status) {
      case AgFinancingPhase4Status.AWAITING_ASSIGNMENT:
        setNotes(undefined);
        setNotificationProps({
          title: i18next.t('waitingCessionToBeStarted'),
          status: 'warning'
        });
        break;

      case AgFinancingPhase4Status.ASSIGNED_TO_FUND:
        setNotes(phase4Info?.sentToFundNotes);
        setNotificationProps({
          title: i18next.t('waitingFundInformation'),
          subtitle: i18next.t('waitingFundInformationSubtitle'),
          status: 'warning',
          updatedAt: phase4Info?.sentToFundAt,
          updateBy: UserUtil.getUserFullName(phase4Info?.sentToFundBy)
        });
        break;

      case AgFinancingPhase4Status.RECEIVED_BY_FUND:
        setNotes(phase4Info?.receivedByFundNotes);
        setNotificationProps({
          title: i18next.t('waitingFundInformationReview'),
          status: 'warning',
          updatedAt: phase4Info?.receivedByFundAt,
          updateBy: UserUtil.getUserFullName(phase4Info?.receivedByFundBy)
        });
        break;

      case AgFinancingPhase4Status.AWAITING_MANAGER_APPROVAL:
        setNotes(phase4Info?.sentToManagerNotes);
        setNotificationProps({
          title: i18next.t('waitingManagerApproval'),
          status: 'warning',
          updatedAt: phase4Info?.sentToManagerAt,
          updateBy: UserUtil.getUserFullName(phase4Info?.sentToManagerBy)
        });
        break;

      case AgFinancingPhase4Status.DENIED:
        setNotes(phase4Info?.deniedNotes);
        setNotificationProps({
          title: text.firstLetterUppercase(`${i18next.t('cession')} ${i18next.t('denied', { context: 'female' })}`),
          status: 'error',
          updatedAt: phase4Info?.deniedAt,
          updateBy: UserUtil.getUserFullName(phase4Info?.deniedBy)
        });
        break;

      case AgFinancingPhase4Status.SUBMITTED_MINUTA:
        setNotes(phase4Info?.submittedNotes);
        setNotificationProps({
          title: i18next.t('submittedMinuta'),
          status: 'success',
          updatedAt: phase4Info?.submittedAt,
          updateBy: UserUtil.getUserFullName(phase4Info?.submittedBy)
        });
        break;

      default:
        setNotes(undefined);
        setNotificationProps(defaultNotificationProp);
    }
  };

  return !!cessionGroup.status ? (
    <NotificationSection {...notificationProps}>
      {!!notes ? <ReadOnlyField label={i18next.t('notes')} value={notes} /> : null}
    </NotificationSection>
  ) : null;
};
